
import dayjs from "dayjs";

/**
 * Replicated helper from KeyMetrics/index.js
 */
export const getFollowerValue = (item, platform) => {
  if (!item) return { value: null, isPredicted: false };
  
  const isYouTube = platform?.toLowerCase() === "youtube";
  const metricKey = isYouTube ? "subscribers" : "followers";
  const predictedMetricKey = isYouTube ? "predicted_subscribers" : "predicted_followers";

  const actual = item[metricKey];
  const predicted = item[predictedMetricKey];
  
  if (actual != null && actual > 0) return { value: actual, isPredicted: false };
  if (predicted != null && predicted > 0) return { value: predicted, isPredicted: true };
  if (actual != null) return { value: actual, isPredicted: false };
  if (predicted != null) return { value: predicted, isPredicted: true };
  return { value: null, isPredicted: false };
};

/**
 * Replicated helper from AudienceKeyMetrics.js
 */
export const safePercentageChange = (current, previous) => {
  if (isNaN(current) || isNaN(previous)) return 0;
  if (previous === 0) {
    if (current === 0) return 0;
    return 100;
  }
  const diff = current - previous;
  if (previous < 0 && current > 0) return Math.abs((diff / Math.abs(previous)) * 100);
  if (previous < 0 && current > previous) return Math.abs((diff / Math.abs(previous)) * 100);
  return ((current - previous) / previous) * 100;
};

/**
 * Replicated Hybrid Growth Calculation from AudienceKeyMetrics.js
 * (Ensures T-1 baseline is handled correctly)
 */
export const calculateGrowthFromData = (data, adjustedData, platform) => {
  if (!data || data.length === 0) return 0;

  let totalGrowth = 0;
  data.forEach((item, index) => {
    const currentVal = getFollowerValue(item, platform);
    let diff = 0;

    if (index === 0) {
      // Day 0: Use Adjusted Data (T-1) if available
      let hasT1Baseline = false;
      if (adjustedData && adjustedData.length > 0) {
        const baselineItem = adjustedData[0];
        if (baselineItem.date !== item.date) {
            const prevVal = getFollowerValue(baselineItem, platform);
            if (currentVal.value !== null && prevVal.value !== null) {
                diff = currentVal.value - prevVal.value;
                hasT1Baseline = true;
            }
        }
      }

      // Fallback to granular metrics if T-1 baseline is missing
      if (!hasT1Baseline) {
        const pm = item.profileMetrics || item.pageMetrics || {};
        if (pm.net_followers !== undefined) {
          diff = pm.net_followers;
        } else {
          const gain = pm.followers_gain ?? pm.follower_gain ?? 0;
          const loss = pm.followers_lost ?? 0;
          if (gain !== 0 || loss !== 0) diff = gain - loss;
        }
      }
    } else {
      // Normal Day: Curr - Prev
      const prevVal = getFollowerValue(data[index - 1], platform);
      if (currentVal.value !== null && prevVal.value !== null) {
        diff = currentVal.value - prevVal.value;
      }
    }
    totalGrowth += diff;
  });

  return totalGrowth;
};

/**
 * Replicated Engagement logic
 */
export const calculateAverageEngagementRate = (data, platform) => {
    if (!data || data.length === 0) return 0;

    let totalEngagements = 0;
    let followerSum = 0;
    let followerDays = 0;

    data.forEach(item => {
        // Simple sum of likes/comments as per platformUtils
        let dayEng = 0;
        if (item.posts) {
            dayEng = item.posts.reduce((sum, p) => {
                const pm = p.postMetrics || {};
                return sum + 
                    (pm.likes || pm.like || 0) + 
                    (pm.love || 0) + 
                    (pm.wow || 0) + 
                    (pm.haha || 0) + 
                    (pm.sad || 0) + 
                    (pm.angry || 0) + 
                    (pm.comments || 0) +
                    (pm.shares || 0);
            }, 0);
        } else if (item.videos) {
            dayEng = item.videos.reduce((sum, v) => {
                const vm = v.videoMetrics || {};
                return sum + (vm.likeCount || 0) + (vm.commentCount || 0) + (vm.shareCount || 0);
            }, 0);
        }
        
        totalEngagements += dayEng;
        const followers = getFollowerValue(item, platform).value;
        if (followers > 0) {
            followerSum += followers;
            followerDays += 1;
        }
    });

    const lastDayFollowers = getFollowerValue(data.at(-1), platform).value || 0;
    return lastDayFollowers > 0 ? (totalEngagements / lastDayFollowers) * 100 : 0;
};

/**
 * Detects posts that likely caused significant follower fluctuations.
 */
export const findHighImpactPosts = (data, platform) => {
    if (!data || data.length < 2) return [];

    const impactPosts = [];
    const dailyChanges = [];

    // 1. Calculate daily net changes
    for (let i = 1; i < data.length; i++) {
        const current = getFollowerValue(data[i], platform).value;
        const previous = getFollowerValue(data[i-1], platform).value;
        if (current !== null && previous !== null) {
            dailyChanges.push({
                date: data[i].date,
                index: i,
                change: current - previous
            });
        }
    }

    if (dailyChanges.length === 0) return [];

    // --- 1. Calculate Profile-Wide Average Engagement ---
    let totalEng = 0;
    let postCount = 0;
    data.forEach(day => {
        (day.posts || day.videos || []).forEach(p => {
            const m = p.postMetrics || p.videoMetrics || {};
            totalEng += (m.likes || m.like || m.likeCount || 0) + (m.comments || m.commentCount || 0);
            postCount++;
        });
    });
    const profileAvgEng = postCount > 0 ? totalEng / postCount : 0;

    // 2. Find significant spikes and drops
    const sorted = [...dailyChanges].sort((a, b) => Math.abs(b.change) - Math.abs(a.change));
    const topVolatileDays = sorted.slice(0, 5); // Increased to 5 for better coverage

    topVolatileDays.forEach(day => {
        const windowIndices = [day.index, day.index - 1, day.index - 2].filter(idx => idx >= 0);
        
        windowIndices.forEach(idx => {
            const dayData = data[idx];
            const posts = dayData.posts || dayData.videos || [];
            
            posts.forEach(post => {
                const m = post.postMetrics || post.videoMetrics || {};
                const eng = (m.likes || m.like || m.likeCount || 0) + (m.comments || m.commentCount || 0);
                
                // Calculate Relative Performance vs. Profile Average
                const relPerf = profileAvgEng > 0 ? (eng / profileAvgEng) : 1;

                impactPosts.push({
                    date: dayData.date,
                    impactDate: day.date,
                    change: day.change,
                    type: post.post_type || post.media_type || post.video_type || "Post",
                    engagement: eng,
                    relativePerformance: relPerf,
                    title: post.title || post.caption || post.message || post.text || "unnamed content"
                });
            });
        });
    });

    // --- 3. Super-Viral Catch: Include any post > 5x avg regardless of day volatility ---
    data.forEach(day => {
        const posts = day.posts || day.videos || [];
        posts.forEach(post => {
            const m = post.postMetrics || post.videoMetrics || {};
            const eng = (m.likes || m.like || m.likeCount || 0) + (m.comments || m.commentCount || 0);
            const relPerf = profileAvgEng > 0 ? (eng / profileAvgEng) : 1;

            if (relPerf > 5) { // 5x better than average is a major viral win
                impactPosts.push({
                    date: day.date,
                    impactDate: day.date,
                    change: 0, // No single-day spike required for viral catch
                    type: post.post_type || post.media_type || post.video_type || "Post",
                    engagement: eng,
                    relativePerformance: relPerf,
                    title: post.title || post.caption || post.message || post.text || "unnamed content"
                });
            }
        });
    });

    // Deduplicate and prioritize by relative performance + volume
    const uniquePosts = Array.from(new Map(impactPosts.map(p => [p.date + p.title, p])).values());
    return uniquePosts
        .sort((a, b) => (b.relativePerformance + (Math.abs(b.change) > 0 ? 1 : 0)) - (a.relativePerformance + (Math.abs(a.change) > 0 ? 1 : 0)))
        .slice(0, 10); // Increase returned posts for better selection downstream
};

/**
 * Orchestrator: Combines all logic to return a profile metrics object
 * mirrors the backend structure for narrative compatibility.
 */
export const computeProfileMetrics = (profile, datasets, previousdatasets, adjustedcurrentdatasets, adjustedpreviousdatasets) => {
    const currentProfile = datasets.find((p) => p.profileId === profile.profileId);
    const previousProfile = previousdatasets.find((p) => p.profileId === profile.profileId);
    const adjustedCurrent = adjustedcurrentdatasets.find((p) => p.profileId === profile.profileId);
    const adjustedPrevious = adjustedpreviousdatasets.find((p) => p.profileId === profile.profileId);

    const data = currentProfile?.data || [];
    const adjData = adjustedCurrent?.data || [];
    const prevData = previousProfile?.data || [];
    const adjPrevData = adjustedPrevious?.data || [];

    const platform = profile.platform;
    
    // Audience
    const growth = calculateGrowthFromData(data, adjData, platform);
    const prevGrowth = calculateGrowthFromData(prevData, adjPrevData, platform);
    
    const lastDayFollowers = getFollowerValue(data.at(-1), platform).value || 0;
    const firstDayFollowers = getFollowerValue(adjData[0], platform).value || 0;
    const growthPct = firstDayFollowers ? ((lastDayFollowers - firstDayFollowers) / firstDayFollowers) * 100 : 0;

    // Engagement
    let totalEngagements = 0;
    const typeEngagements = {};

    data.forEach(item => {
        let dayEng = 0;
        const posts = item.posts || item.videos || [];
        
        posts.forEach(p => {
            const m = p.postMetrics || p.videoMetrics || {};
            const eng = (m.likes || m.like || m.likeCount || 0) + (m.comments || m.commentCount || 0);
            dayEng += eng;
            
            const type = (p.post_type || p.media_type || p.video_type || "Post").toLowerCase();
            typeEngagements[type] = (typeEngagements[type] || 0) + eng;
        });
        totalEngagements += dayEng;
    });

    const rateAvg = calculateAverageEngagementRate(data, platform);
    
    // Day of Week Resonance Calculation
    const dowStats = {};
    ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].forEach(d => {
        dowStats[d] = { total: 0, count: 0 };
    });

    data.forEach(day => {
        const dayName = dayjs(day.date).format('dddd');
        const posts = day.posts || day.videos || [];
        posts.forEach(p => {
            const m = p.postMetrics || p.videoMetrics || {};
            const eng = (m.likes || m.like || m.likeCount || 0) + (m.comments || m.commentCount || 0) + (m.shares || m.shareCount || 0);
            dowStats[dayName].total += eng;
            dowStats[dayName].count += 1;
        });
    });

    const dowResonance = Object.entries(dowStats)
        .map(([day, stats]) => ({ day, avg: stats.count > 0 ? stats.total / stats.count : 0, count: stats.count }))
        .filter(d => d.count > 0)
        .sort((a, b) => b.avg - a.avg);

    // Content Attribution
    const impactPosts = findHighImpactPosts(data, platform);
    const totalPublished = data.reduce((sum, d) => sum + (d.posts?.length || d.videos?.length || 0), 0);
    
    let topType = "N/A";
    let maxEng = -1;
    Object.entries(typeEngagements).forEach(([type, eng]) => {
        if (eng > maxEng) {
            maxEng = eng;
            topType = type;
        }
    });

    const topTypeCount = data.reduce((sum, d) => sum + (d.posts || d.videos || []).filter(p => (p.post_type || p.media_type || p.video_type || "Post").toLowerCase() === topType).length, 0);
    const typeConcentration = totalPublished > 0 ? (topTypeCount / totalPublished) : 0;

    return {
        brandName: profile.brandName || profile.label,
        isPrimary: profile.isPrimary || (datasets.length === 1),
        platform: platform,
        audience: {
            end: lastDayFollowers,
            growthPct: growthPct,
            net: growth
        },
        engagement: {
            rateAvg: rateAvg,
            avgEngagement: totalPublished > 0 ? totalEngagements / totalPublished : 0
        },
        content: {
            totalPublished: totalPublished,
            topType: topType,
            typeConcentration: typeConcentration,
            impactPosts: impactPosts,
            bestDay: dowResonance[0],
            worstDay: dowResonance.length > 2 ? dowResonance.at(-1) : null
        }
    };
};
